import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import { Box } from '@mui/material';
import { makeStyles } from '@mui/styles';
import createStyles from '@mui/styles/createStyles';
import {
    Border,
    FontVariantToken,
    Spacing,
} from '@uipath/apollo-core';
import {
    ApDateRangePicker,
    ApTooltip,
    ApTypography,
} from '@uipath/portal-shell-react';
import type { DateRangeValue } from '@uipath/portal-shell-types';
import { PresetOptionLabel } from '@uipath/portal-shell-util/lib/constants/DateTimeConstants';
import React, {
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import { useIntl } from 'react-intl';

import { useConsumptionTimezone } from '../hooks/useConsumptionTimezone';
import { normalizeWallClock } from '../TenantLevelDashboard.utils';

// The picker emits offset/millis ISO strings and zeroes seconds (minute-granular UI), while
// props may carry :59 ends — compare at minute precision so an unchanged Apply is a no-op.
const toRangeKey = (start: string, end: string): string =>
    `${(normalizeWallClock(start) ?? start).slice(0, 16)}|${(normalizeWallClock(end) ?? end).slice(0, 16)}`;

const useStyles = makeStyles(theme =>
    createStyles({
        pickerRow: {
            display: 'flex',
            alignItems: 'center',
            gap: Spacing.SpacingXs,
        },
        controlsDivider: { color: theme.palette.semantic.colorForegroundDeEmp },
        resetButton: {
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            padding: 0,
            borderRadius: Border.BorderRadiusM,
            color: theme.palette.semantic.colorPrimary,
            '&:hover': { backgroundColor: theme.palette.semantic.colorBackgroundHover },
        },
        infoRow: {
            display: 'flex',
            alignItems: 'center',
            gap: Spacing.SpacingBase,
            flexWrap: 'wrap',
        },
        infoItem: {
            display: 'flex',
            alignItems: 'center',
            gap: Spacing.SpacingXs,
        },
        infoIcon: {
            width: '16px',
            height: '16px',
            color: theme.palette.semantic.colorForegroundDeEmp,
        },
        infoText: { color: theme.palette.semantic.colorForegroundDeEmp },
    })
);

interface ConsumptionDateRangePickerProps {
    bundleStartDate: string;
    bundleEndDate: string;
    periodDisplay: string;
    initialStartDate?: string;
    initialEndDate?: string;
    // Earliest selectable day (YYYY-MM-DD). Unset keeps the picker open-ended into the past.
    pastThreshold?: string;
    onDateRangeChange?: (startDate: string, endDate: string) => void;
    onReset?: () => void;
}

export const ConsumptionDateRangePicker: React.FC<ConsumptionDateRangePickerProps> = ({
    bundleStartDate,
    bundleEndDate,
    periodDisplay,
    initialStartDate,
    initialEndDate,
    pastThreshold,
    onDateRangeChange,
    onReset,
}) => {
    const classes = useStyles();
    const { formatMessage: translate } = useIntl();
    const { timezoneId } = useConsumptionTimezone();

    const [ selectedRange, setSelectedRange ] = useState<DateRangeValue>(() => ({
        label: PresetOptionLabel.Custom,
        range: {
            start: initialStartDate ?? bundleStartDate,
            end: initialEndDate ?? bundleEndDate,
        },
    }));

    const lastEmittedRangeRef = useRef<string>(
        toRangeKey(initialStartDate ?? bundleStartDate, initialEndDate ?? bundleEndDate)
    );

    const bundleRangeValue = useMemo<DateRangeValue>(() => ({
        label: PresetOptionLabel.Custom,
        range: {
            start: bundleStartDate,
            end: bundleEndDate,
        },
    }), [ bundleStartDate, bundleEndDate ]);

    const futureThreshold = useMemo(() => {
        // en-CA yields YYYY-MM-DD
        const today = new Intl.DateTimeFormat('en-CA', {
            timeZone: timezoneId,
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
        }).format(Date.now());
        return bundleEndDate > today ? bundleEndDate : today;
    }, [ bundleEndDate, timezoneId ]);

    useEffect(() => {
        const start = initialStartDate ?? bundleStartDate;
        const end = initialEndDate ?? bundleEndDate;

        if (!start || !end) {
            return;
        }

        const nextKey = toRangeKey(start, end);

        if (nextKey !== lastEmittedRangeRef.current) {
            lastEmittedRangeRef.current = nextKey;
            setSelectedRange({
                label: PresetOptionLabel.Custom,
                range: {
                    start,
                    end,
                },
            });
        }
    }, [ initialStartDate, initialEndDate, bundleStartDate, bundleEndDate ]);

    useEffect(() => {
        const start = selectedRange.range.start;
        const end = selectedRange.range.end;

        if (!start || !end) {
            return;
        }

        const nextKey = toRangeKey(start, end);
        if (nextKey === lastEmittedRangeRef.current) {
            return;
        }

        lastEmittedRangeRef.current = nextKey;
        onDateRangeChange?.(normalizeWallClock(start) ?? start, normalizeWallClock(end) ?? end);
    }, [ selectedRange, onDateRangeChange ]);

    const handleReset = () => {
        setSelectedRange(bundleRangeValue);
        lastEmittedRangeRef.current = toRangeKey(bundleStartDate, bundleEndDate);
        onReset?.();
    };

    const hasCustomRange = selectedRange.range.start !== bundleStartDate
        || selectedRange.range.end !== bundleEndDate;

    return (
        <>
            <Box className={classes.pickerRow}>
                <ApDateRangePicker
                    variant="filter"
                    label={translate({
                        id: hasCustomRange
                            ? 'CLIENT_CONSUMPTION_DASHBOARD_CUSTOM_RANGE_LABEL'
                            : 'CLIENT_CONSUMPTION_DASHBOARD_ORG_LABEL_CURRENT_BUNDLE',
                    })}
                    rangeValue={selectedRange}
                    onRangeValueChanged={(event) => {
                        const eventDetail = event.detail as DateRangeValue | undefined;
                        if (!eventDetail?.range) {
                            return;
                        }
                        setSelectedRange(eventDetail);
                    }}
                    pastThreshold={pastThreshold}
                    futureThreshold={futureThreshold}
                    includeTime
                    useExtraOptions
                    hideAllOption
                />
                <ApTypography
                    className={classes.controlsDivider}
                    variant={FontVariantToken.fontSizeS}
                >
                    |
                </ApTypography>
                <ApTooltip content={translate({ id: 'CLIENT_CONSUMPTION_DASHBOARD_TENANT_RESET_TOOLTIP' })}>
                    <button
                        type="button"
                        className={classes.resetButton}
                        onClick={handleReset}
                    >
                        <ApTypography variant={FontVariantToken.fontSizeMBold}>
                            {translate({ id: 'CLIENT_RESET' })}
                        </ApTypography>
                    </button>
                </ApTooltip>
            </Box>
            <Box className={classes.infoRow}>
                <Box className={classes.infoItem}>
                    <CalendarMonthIcon className={classes.infoIcon} />
                    <ApTypography
                        variant={FontVariantToken.fontSizeS}
                        className={classes.infoText}
                    >
                        {`${translate({ id: 'CLIENT_CONSUMPTION_DASHBOARD_ORG_LABEL_CURRENT_BUNDLE' })} : ${periodDisplay}`}
                    </ApTypography>
                </Box>
            </Box>
        </>
    );
};
